/**
 * Slug utilities for course URLs.
 *
 * IMPORTANT: This logic MUST stay identical to the backend implementation
 * in ai_course_backend/server.js (generateSlug, extractIdFromSlug).
 * If you change one, change the other — mismatches cause canonical URL
 * issues, redirect loops, and broken share links.
 */

/**
 * Transliteration map for accented Latin characters.
 * Identical to the backend map in server.js.
 */
const translitMap = {
    'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a', 'ä': 'a', 'å': 'a', 'ă': 'a', 'ą': 'a',
    'ć': 'c', 'č': 'c', 'ç': 'c',
    'ď': 'd', 'ð': 'd',
    'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e', 'ě': 'e', 'ę': 'e',
    'ğ': 'g',
    'î': 'i', 'ì': 'i', 'í': 'i', 'ï': 'i',
    'ł': 'l',
    'ñ': 'n', 'ň': 'n', 'ń': 'n',
    'ò': 'o', 'ó': 'o', 'ô': 'o', 'õ': 'o', 'ö': 'o', 'ø': 'o',
    'ř': 'r',
    'ś': 's', 'š': 's', 'ș': 's', 'ş': 's',
    'ť': 't', 'ț': 't', 'ţ': 't',
    'þ': 'th',
    'ù': 'u', 'ú': 'u', 'û': 'u', 'ü': 'u', 'ů': 'u',
    'ý': 'y', 'ÿ': 'y',
    'ž': 'z', 'ź': 'z', 'ż': 'z',
    'ß': 'ss',
};

/**
 * Convert a course title + MongoDB ObjectId into a URL-safe slug.
 *
 * @param {string|null|undefined} mainTopic - Course title. Falls back to 'course'.
 * @param {string} id - MongoDB ObjectId as 24-char hex string.
 * @returns {string} URL-safe slug in the form "{topic}-{id}".
 *
 * @example
 * generateSlug('Machine Learning Basics', '6571a2abc123def456789012')
 * // → 'machine-learning-basics-6571a2abc123def456789012'
 *
 * @example
 * generateSlug('Învățare Automată', '6571a2abc123def456789012')
 * // → 'invatare-automata-6571a2abc123def456789012'
 */
export function generateSlug(mainTopic, id) {
    let topic = (mainTopic || 'course')
        .toLowerCase()
        .replace(/./g, c => translitMap[c] || c)
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 60);
    if (!topic) topic = 'course';
    return `${topic}-${id}`;
}

/**
 * Extract the 24-character hex MongoDB ObjectId from the tail of a slug.
 *
 * @param {string|null|undefined} slug - URL slug (e.g. "python-basics-aabb...").
 * @returns {string|null} The 24-char hex ObjectId, or null if invalid/missing.
 */
export function extractIdFromSlug(slug) {
    const match = (slug || '').match(/([a-f0-9]{24})$/);
    return match ? match[1] : null;
}

/**
 * Build the public preview URL path for a course.
 * Returns a relative path — caller is responsible for prepending the domain
 * (e.g. via websiteURL from constants.js) when an absolute URL is needed.
 *
 * @param {string|null|undefined} mainTopic - Course title.
 * @param {string} courseId - MongoDB ObjectId as 24-char hex string.
 * @returns {string} URL path like "/c/machine-learning-basics-6571a2abc123def456789012".
 */
export function buildCourseUrl(mainTopic, courseId) {
    return `/c/${generateSlug(mainTopic, courseId)}`;
}
